<template>
  <ValidationObserver v-slot="validator">
    <!-- 
      Using the computed property "criterionTitle" for modalContent instead of directly binding
      to dirtyCriterion.content.title prevents Vue prop mutation warnings and follows
      the recommended pattern for one-way data flow.
    -->
    <InteractiveModal
      :modalId="'auditCriteriaModal'"
      :modalTitle="modalTitle"
      :modalContent="criterionTitle"
      :isModalActive="isModalActive"
      :isMinimized="isMinimized"
      :draggable="true"
      :isDirty="isFormDirty"
      @close="$emit('close')"
      @minimize="$emit('minimize')"
      @restore="$emit('restore')"
    >
      <template slot="modal-body">
        <ValidationProvider
          v-slot="{ errors }"
          rules="required|max:255"
          name="Criterion Inquiry"
        >
          <b-field
            label="Question or Inquiry"
            :type="errors.length ? 'is-danger' : undefined"
            :message="errors[0]"
          >
            <b-input v-model="dirtyCriterion.content.title" />
          </b-field>
        </ValidationProvider>
        <b-field label="Type of Answer">
          <b-dropdown
            aria-role="list"
            v-model="dirtyCriterion.content.type"
            class="audit-criterion-dropdown-container"
          >
            <template #trigger="{ active }">
              <button class="button audit-criterion-type-dropdown">
                <span>{{
                  dirtyCriterion.content.label
                    ? getFormattedText(dirtyCriterion.content.label)
                    : ''
                }}</span>
                <b-icon :icon="active ? 'menu-up' : 'menu-down'"></b-icon>
              </button>
            </template>

            <b-dropdown-item
              aria-role="listitem"
              v-for="option in options"
              :key="option.id"
              :value="option.id"
              class="audit-criterion-type-dropdown-item"
            >
              {{ option.name }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
        <!-- 
          IMPROVEMENTS:
          1. Replaced inline v-if conditions with computed properties for better readability
          2. Fixed .sync binding by using the expanded form with separate property binding and event handler
          This helps prevent template compilation errors and makes the data flow more explicit
        -->
        <BooleanCriterionInput
          v-if="isBooleanContent"
          :options="booleanContent.booleanOptions"
          @update:options="booleanContent.booleanOptions = $event"
          @disableSubmitButton="disableBooleanSubmit"
        />
        <RangeCriterionInput
          v-if="isRangeContent"
          :range="rangeContent.range"
          @update:range="rangeContent.range = $event"
          @disableSubmitButton="disableRangeSubmit"
        />
        <CheckboxesCriterionInput
          v-if="isCheckboxesContent"
          :options="checkboxesContent.checkboxesOptions"
          @update:options="checkboxesContent.checkboxesOptions = $event"
        />
        <ContinuousDiscreteCriterionInput
          v-if="isContinuousDiscreteContent"
          :dirtyCriterion="dirtyCriterion"
          @update:dirtyCriterion="dirtyCriterion = $event"
          @disableSubmitButton="disableRangeSubmit"
        />
        <b-field
          :class="$style.sourceLinkInputContainer"
          label="Source Link"
          v-if="showSourceLinkInput"
        >
          <b-input v-model="dirtyCriterion.content.sourceLink" />
        </b-field>
        <div v-if="showSourceLinkInput && dirtyCriterion.content.sourceLink">
          <a target="_blank" :class="$style.sourceLink" :href="sourceLink">
            Go to source link
            <b-icon icon="launch" size="is-small"></b-icon>
          </a>
        </div>
        <b-field v-if="isNew" label="Criteria Guide">
          <b-input v-model="dirtyCriterion.content.guide" type="textarea" />
        </b-field>
        <div
          :class="$style.applyChangesContainer"
          v-if="!createProject && projectId"
        >
          <b-field>
            <b-checkbox v-model="applyChanges">
              Apply changes to other data collection periods
              <b-tooltip
                label="This list exludes data collection periods that have already commenced or ended."
                multilined
              >
                <b-icon
                  pack="mdi"
                  icon="information-outline"
                  size="is-small"
                  style="color: #848e9f"
                />
              </b-tooltip>
            </b-checkbox>
          </b-field>
          <template :class="$style.auditsContainer" v-if="applyChanges">
            <div
              v-if="isAuditSelectionMissing"
              :class="$style.missingAuditSelectionNotification"
            >
              <b-icon icon="information" style="color: #334d7c" />
              <p>
                Please select at least one data collection period to apply
                changes. To continue without applying changes, deselect the
                option above.
              </p>
            </div>
            <b-field :class="$style.eligibleAuditsContainer">
              <div
                :class="$style.selectAllCheckboxContainer"
                :style="{ backgroundColor: checkedAll ? '#E6E9EF' : '#FAFBFB' }"
              >
                <b-checkbox
                  v-model="checkedAll"
                  @input="toggleSelectAllAudits"
                  :disabled="
                    !availableAudits || availableAudits.totalItems === 0
                  "
                  :key="checkboxKey"
                >
                  {{ selectedAuditIds.length }} selected
                </b-checkbox>
              </div>
              <div
                v-for="audit in currentPageAudits"
                :key="audit.id"
                :class="$style.eligibleAudits"
              >
                <b-checkbox v-model="selectedAuditIds" :native-value="audit.id">
                  {{ audit.name }}
                </b-checkbox>
              </div>
              <div
                v-if="displayedAudits.length === 0"
                :class="$style.noAvailableAuditsContainer"
              >
                <p>No audits available</p>
              </div>
            </b-field>
            <BasePagination
              :class="$style.customPaginationOverride"
              :page.sync="getCurrentPage"
              :perPage.sync="getCurrentPerPage"
              :totalNumberOfPage="totalNumberOfPage"
              :totalCount="getTotalCount"
              :isFirstPage="isFirstPage"
              :isLastPage="isLastPage"
              :startItemIndex="startItemIndex"
              :endItemIndex="endItemIndex"
              :show-per-page-options="false"
              v-if="getTotalCount > 10"
            />
            <div :class="$style.ineligibleAuditsContainer">
              <a
                @click="openIneligibleDataCollectionPeriods"
                class="button is-link is-text-primary is-borderless is-transparent"
                >View unavailable data collection periods</a
              >
            </div>
          </template>
        </div>
      </template>
      <template slot="modal-footer">
        <button
          v-if="isNew"
          :class="['button', 'is-primary', 'is-rounded', $style.addCriteria]"
          :disabled="validator.invalid || hasRangeError || hasBooleanError"
          @click="handleCreateClick(validator)"
        >
          Create Custom Criteria
        </button>

        <button
          v-else
          class="button is-primary"
          :disabled="
            validator.invalid ||
            hasRangeError ||
            hasBooleanError ||
            isBtnDisabled ||
            isAuditSelectionMissing
          "
          @click="handleEditClick(validator)"
        >
          Save Changes
        </button>
      </template>
    </InteractiveModal>
  </ValidationObserver>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import InteractiveModal from '@/components/base/InteractiveModal.vue';
import {
  CRITERION_TYPE_DISPLAY_NAME,
  CRITERION_TYPE,
  Criterion,
  BooleanCriterionContent,
  RangeCriterionContent,
  CheckboxesCriterionContent,
  CheckboxesSingleCriterionContent,
  ContinuousDiscreteCriterionContent,
} from '@/store/types/criterions.types';
import { StringInputOption } from '@/jbi-shared/types/form.types';
import {
  cloneDeep as _cloneDeep,
  isEqual as _isEqual,
  uniq as _uniq,
  merge as _merge,
} from 'lodash';
import { DialogProgrammatic as Dialog } from 'buefy';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import CheckboxesCriterionInput from '@/views/Project/components/CheckboxesCriterionInput.vue';
import BooleanCriterionInput from '@/views/Project/components/BooleanCriterionInput.vue';
import RangeCriterionInput from '@/views/Project/components/RangeCriterionInput.vue';
import ContinuousDiscreteCriterionInput from '@/views/Project/components/ContinuousDiscreteCriterionInput.vue';
import { CplusCriteria } from '@/store/modules/projects/types/projects.types';
import ConfirmationDialogue from '../../../components/ConfirmationDialogue.vue';
import UnavailableCyclesDialogue from './UnavailableCyclesDialogue.vue';
import { AuditDetail } from '@/store/modules/audits/types/audits.types';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import {
  AuditAvailabilityForCriteriaUpdate,
  GetFilteredAuditsByCriteriaPayload,
} from '@/store/modules/projects/types/projects.types';
import { ApiState, Pagination } from '@/store/types/general.types';
import BasePagination from '@/components/base/BasePagination.vue';
import { createDefaultCriterion } from '@/views/Project/utils/default-criterion.util';

@Component({
  components: {
    CheckboxesCriterionInput,
    BooleanCriterionInput,
    RangeCriterionInput,
    ValidationProvider,
    ValidationObserver,
    ContinuousDiscreteCriterionInput,
    InteractiveModal,
    BasePagination,
  },
})
export default class AuditCriteriaCreateModal extends Vue {
  @Prop()
  public modalTitle!: string;

  @Prop({
    default: createDefaultCriterion,
  })
  public criterion!: Criterion;

  @Prop()
  public reOrderedCriteriaArray!: any;

  @Prop()
  public criteriaIndex!: number;

  @Prop({
    default() {
      return false;
    },
  })
  public isSavedCriteria!: boolean;

  @Prop()
  public currentCriteria!: CplusCriteria;

  @Prop({ default: true })
  public projectEdit!: boolean;

  @Prop()
  public createProject!: boolean;

  @Prop()
  public isProject!: boolean;

  @Prop({ default: false })
  public isMinimized!: boolean;

  @Prop({ default: true })
  public isModalActive!: boolean;

  @Prop()
  public projectId!: number;

  @Prop()
  public auditId!: number;

  @Action('projects/getAvailableFilteredAudits')
  public getAvailableFilteredAudits!: (
    payload: GetFilteredAuditsByCriteriaPayload,
  ) => void;

  @State((state: RootState) => state.projects.availableAudits)
  public availableAudits!: Pagination<AuditDetail>;

  @State(
    (state: RootState) => state.projects.apiState.getAvailableFilteredAudits,
  )
  public availableAuditsApiState!: ApiState;

  public dirtyCriterion!: Criterion;
  public dirtyCriterions!: any;

  public data(): { dirtyCriterion: Criterion } {
    return {
      dirtyCriterion: _cloneDeep(this.criterion ?? createDefaultCriterion()),
    };
  }

  @Watch('criterion')
  public onCriterionPropChanged(): void {
    this.dirtyCriterion = _cloneDeep(
      this.criterion ?? createDefaultCriterion(),
    );
  }

  public selectedAuditIds: number[] = [];
  public displayedAudits: AuditDetail[] = [];
  public currentPageAudits: AuditDetail[] = [];

  public checkboxKey: number = Math.floor(Math.random() * 999);

  public hasRangeError: boolean = false;
  public hasBooleanError: boolean = false;
  public isBtnDisabled: boolean = true;
  public applyChanges: boolean = false;

  public filter!: AuditAvailabilityForCriteriaUpdate;
  public perPage: number = 10;
  public page: number = 1;
  public totalCount: number = 0;
  public visitedPages: Set<number> = new Set();

  get totalNumberOfPage(): number {
    return Math.ceil(this.getTotalCount / this.perPage);
  }

  get startItemIndex(): number {
    return this.page * this.perPage - this.perPage + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.getTotalCount, this.page * this.perPage);
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  get isLastPage(): boolean {
    return this.page === this.totalNumberOfPage;
  }

  get getCurrentPage(): number {
    return this.page;
  }

  get getTotalCount(): number {
    if (!this.availableAudits) {
      return 0;
    }
    return this.availableAudits.totalItems != null
      ? this.availableAudits.totalItems
      : 0;
  }

  get CRITERION_TYPE() {
    return CRITERION_TYPE;
  }

  set getCurrentPage(newValue: number) {
    this.page = Number(newValue);
    // Fetch new page data instead of slicing the Pagination object
    if (this.applyChanges) {
      const params: GetFilteredAuditsByCriteriaPayload = {
        projectId: this.projectId,
        criterionId: this.criterion.id!,
        filter: AuditAvailabilityForCriteriaUpdate.AVAILABLE,
        limit: this.perPage,
        page: this.page,
        // If we are editing the current audit, include its id in the payload
        currentAuditId: this.isProject ? undefined : this.auditId,
      };
      this.getAvailableFilteredAudits(params);
    }
  }

  get getCurrentPerPage(): number {
    return this.perPage;
  }

  /**
   * Computed properties for content type checking
   * IMPROVEMENTS:
   * 1. Extracted conditional logic from the template to computed properties
   * 2. Improves readability and maintainability of the template
   * 3. Enables easier testing and reuse of the conditions
   */
  get isBooleanContent(): boolean {
    return this.dirtyCriterion.content.type === CRITERION_TYPE.BOOLEAN;
  }

  get isRangeContent(): boolean {
    return this.dirtyCriterion.content.type === CRITERION_TYPE.RANGE;
  }

  get isCheckboxesContent(): boolean {
    return (
      this.dirtyCriterion.content.type === CRITERION_TYPE.CHECKBOXES ||
      this.dirtyCriterion.content.type === CRITERION_TYPE.CHECKBOXES_SINGLE
    );
  }

  get isContinuousDiscreteContent(): boolean {
    return (
      this.dirtyCriterion.content.type === CRITERION_TYPE.CONTINUOUS_DISCRETE
    );
  }

  get booleanContent(): BooleanCriterionContent {
    return this.dirtyCriterion.content as BooleanCriterionContent;
  }

  get rangeContent(): RangeCriterionContent {
    return this.dirtyCriterion.content as RangeCriterionContent;
  }

  get checkboxesContent(): CheckboxesCriterionContent {
    return this.dirtyCriterion.content as CheckboxesCriterionContent;
  }

  get options(): StringInputOption[] {
    return Object.entries(CRITERION_TYPE_DISPLAY_NAME).map(([key, value]) => {
      return {
        id: key,
        name: value,
      };
    });
  }

  get isAuditSelectionMissing(): boolean {
    return (
      this.applyChanges &&
      this.selectedAuditIds.length === 0 &&
      this.hasAnySelectableAudits
    );
  }

  /**
   * Determines if there are any selectable audits that are not the current audit.
   * If the total count is 0, this will return false.
   * If the total count is greater than 1, this will return true.
   * If the total count is 1, this will check if the single item is not the current audit
   * If the items have not been loaded yet, this will return false.
   */
  get hasAnySelectableAudits(): boolean {
    const total = this.getTotalCount;
    if (total === 0) {
      return false;
    }
    if (total > 1) {
      return true;
    }

    // If total is 1, we must check if that single item is the current audit.
    // We can only do this if the item has been loaded from the API.
    const items = this.availableAudits.items;
    if (items && items.length === 1) {
      return items[0].id !== this.auditId;
    }

    // Default to false if the check can't be completed (e.g., items not loaded yet).
    return false;
  }

  public reset() {
    this.onCriterionPropChanged();
  }

  public async addCriterion(): Promise<boolean> {
    if (!(await this.validatePreSave())) {
      return false;
    }
    const dirtyCriterion = this.sanitizeCriterion(this.dirtyCriterion);
    if (dirtyCriterion.content instanceof CheckboxesCriterionContent) {
      const [first, ...rest] = dirtyCriterion.content.checkboxesOptions;
      dirtyCriterion.content.checkboxesOptions = [...rest, first];
    }
    this.$emit('newCriteria', {
      id: '',
      ...dirtyCriterion.content,
    });
    return true;
  }

  public async editCriterion(): Promise<void> {
    if (!(await this.validatePreSave())) return;

    const isProjectEdit = this.projectEdit;

    const modalTitle = isProjectEdit
      ? 'Confirm Changes?'
      : 'Are you sure you want to customise this criteria?';

    const confirmationText = isProjectEdit
      ? 'You have selected to save changes to subsequent data collections. This action cannot be undone.'
      : 'Customising this criteria will archive the previous version and disable any further data collection against it. You cannot undo this action.';

    this.$buefy.modal.open({
      parent: this,
      component: ConfirmationDialogue,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle,
        confirmationText,
      },
      events: {
        confirm: () => this.updateCriterion(),
      },
    });
  }

  public updateCriterion() {
    const dirtyCriterion = this.sanitizeCriterion(this.dirtyCriterion);
    const updatedDirtyCriterion: CplusCriteria = this.updateDirtyCplusCriteria(
      this.dirtyCriterions[this.criteriaIndex],
      dirtyCriterion.content,
    );
    if (this.createProject) {
      this.dirtyCriterions[this.criteriaIndex] = { ...updatedDirtyCriterion };
      this.dirtyCriterions[this.criteriaIndex].isChanged = true;
      this.$emit('updateReOrderedCriteriaArray', this.dirtyCriterions);
    } else {
      this.dirtyCriterions[this.criteriaIndex] = { ...updatedDirtyCriterion };
      this.dirtyCriterions[this.criteriaIndex].isChanged = true;
      this.$emit('updateCriteria', {
        newValue: this.dirtyCriterions[this.criteriaIndex],
        applyChanges: this.applyChanges,
        // Include current auditId if editing current audit
        selectedAuditIds: this.isProject
          ? this.selectedAuditIds
          : Array.from(new Set([...this.selectedAuditIds, this.auditId])),
        projectId: this.projectId,
      });
    }
  }

  public async validatePreSave() {
    // check duplicated checkboxed options
    if (
      this.dirtyCriterion.content instanceof CheckboxesCriterionContent ||
      this.dirtyCriterion.content instanceof CheckboxesSingleCriterionContent
    ) {
      const { checkboxesOptions } = this.dirtyCriterion.content;
      const sanitized = this.sanitizeCriterionCheckboxesOptions(
        checkboxesOptions,
      );
      if (!_isEqual(checkboxesOptions, sanitized)) {
        const confirm = () =>
          new Promise((resolve) => {
            return Dialog.confirm({
              message: `Empty values and duplicated values will be removed.`,
              confirmText: 'Confirm',
              cancelText: 'Cancel',
              onConfirm() {
                resolve(true);
              },
              onCancel() {
                resolve(false);
              },
            });
          });
        if (!(await confirm())) {
          return false;
        }
      }
    }
    if (this.dirtyCriterion.content instanceof RangeCriterionContent) {
      if (
        Number(this.dirtyCriterion.content.range[1]) <=
        Number(this.dirtyCriterion.content.range[0])
      ) {
        return false;
      }
    }
    return true;
  }

  public sanitizeCriterion(criterion: Criterion): Criterion {
    criterion = _cloneDeep(criterion);
    if (criterion.content instanceof RangeCriterionContent) {
      criterion.content.range = [
        Number(criterion.content.range[0]),
        Number(criterion.content.range[1]),
        String(criterion.content.range[2]),
      ];
    } else if (
      criterion.content instanceof CheckboxesCriterionContent ||
      criterion.content instanceof CheckboxesSingleCriterionContent
    ) {
      criterion.content.checkboxesOptions = this.sanitizeCriterionCheckboxesOptions(
        criterion.content.checkboxesOptions,
      );
    }
    return criterion;
  }

  public sanitizeCriterionCheckboxesOptions(arr: string[]) {
    arr = arr.map((s) => String(s).trim()).filter(Boolean);
    arr = _uniq(arr);
    return arr;
  }

  public updateDirtyCplusCriteria(
    dirtyCriteria: CplusCriteria,
    updatedCriteria: any,
  ): CplusCriteria {
    // Iterate through the object keys of the existing object
    Object.keys(dirtyCriteria).forEach((item: keyof CplusCriteria) => {
      // Find the matched key value from the updatedCriteria
      const matchedObjectKeys = Object.keys(updatedCriteria).find(
        (data: any) => data === item,
      );

      if (matchedObjectKeys) {
        // Update key value from existing object with the updatedCriteria value
        dirtyCriteria[item] = updatedCriteria[item];
      }
      if (
        this.dirtyCriterion.content.type === CRITERION_TYPE.CONTINUOUS_DISCRETE
      ) {
        dirtyCriteria.config = updatedCriteria.config;
        dirtyCriteria.continuousDiscreteOptions =
          updatedCriteria.continuousDiscreteOptions;
      }
    });
    return dirtyCriteria;
  }

  public openIneligibleDataCollectionPeriods() {
    this.$buefy.modal.open({
      parent: this,
      component: UnavailableCyclesDialogue,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Unavailable Data Collection Periods',
        projectId: this.projectId,
        criterionId: this.criterion.id!,
        confirmationButtonText: 'OK',
      },
    });
  }

  public disableRangeSubmit(value: boolean) {
    return (this.hasRangeError = value);
  }

  public disableBooleanSubmit(value: boolean) {
    return (this.hasBooleanError = value);
  }

  @isDifferent
  @Watch('dirtyCriterion.content.type')
  public onTypeChange(type: CRITERION_TYPE) {
    switch (type) {
      case CRITERION_TYPE.BOOLEAN:
        this.dirtyCriterion.content = new BooleanCriterionContent(
          this.dirtyCriterion.content,
        );
        if (!this.isNew) {
          this.dirtyCriterions[this.criteriaIndex].booleanOptions = [];
        }
        break;
      case CRITERION_TYPE.RANGE:
        this.dirtyCriterion.content = new RangeCriterionContent({
          ...this.dirtyCriterion.content,
        });
        if (!this.isNew) {
          this.dirtyCriterions[this.criteriaIndex].range = [];
        }
        break;
      case CRITERION_TYPE.CHECKBOXES:
        this.dirtyCriterion.content = new CheckboxesCriterionContent({
          ...this.dirtyCriterion.content,
        });
        if (!this.isNew) {
          this.dirtyCriterions[this.criteriaIndex].checkboxesOptions = [];
        }
        break;
      case CRITERION_TYPE.CHECKBOXES_SINGLE:
        this.dirtyCriterion.content = new CheckboxesSingleCriterionContent({
          ...this.dirtyCriterion.content,
        });
        if (!this.isNew) {
          this.dirtyCriterions[this.criteriaIndex].checkboxesOptions = [];
        }
        break;
      case CRITERION_TYPE.CONTINUOUS_DISCRETE:
        this.dirtyCriterion.content = new ContinuousDiscreteCriterionContent({
          ...this.dirtyCriterion.content,
        });
        break;
    }
  }

  @Watch('dirtyCriterion', { deep: true })
  public isCriteriaEdited(): void {
    this.isBtnDisabled =
      JSON.stringify(this.dirtyCriterion) !== JSON.stringify(this.criterion)
        ? false
        : true;
  }

  @Watch('applyChanges')
  onApplyChangesChange(newValue: boolean) {
    // If the checkbox is checked, fetch the filtered audits.
    if (newValue) {
      this.selectedAuditIds = [];
      this.visitedPages.clear();
      const params: GetFilteredAuditsByCriteriaPayload = {
        projectId: this.projectId,
        criterionId: this.criterion.id!,
        filter: AuditAvailabilityForCriteriaUpdate.AVAILABLE,
        limit: this.perPage,
        page: this.page,
        // If we are editing the current audit, include its id in the payload
        // This id will be used to filter out the current audit from the available audits list
        currentAuditId: this.isProject ? undefined : this.auditId,
      };
      this.getAvailableFilteredAudits(params);
    }
  }

  /**
   * This watcher listens for changes in the availableAuditsApiState.
   * When the API call is successful and the available audits are loaded,
   * it updates the currentPageAudits and displayedAudits with the
   * fetched items.
   * It also handles the initial auto-selection of audits on the first
   * page load.
   * The auto-selection is done only once per page to avoid re-selecting
   * audits when the user navigates back to a previously visited page.
   */
  @Watch('availableAuditsApiState', { deep: true })
  public onApiStateChange(state: ApiState) {
    if (state.success && this.availableAudits.items) {
      this.currentPageAudits = this.availableAudits.items;
      this.displayedAudits = this.availableAudits.items;
      this.checkboxKey += 1;

      // Only perform the "auto-select all audits" on the initial load
      if (!this.visitedPages.has(this.page)) {
        const selectableAuditIdsOnNewPage = this.availableAudits.items.map(
          (audit) => audit.id,
        );

        const updatedSelectedIds = new Set([
          ...this.selectedAuditIds,
          ...selectableAuditIdsOnNewPage,
        ]);

        this.selectedAuditIds = Array.from(updatedSelectedIds);

        // Set flag to false after initial load is handled
        this.visitedPages.add(this.page);
      }
    }
  }

  public get checkedAll(): boolean {
    if (!this.currentPageAudits || this.currentPageAudits.length === 0) {
      return false;
    }

    const selectableIdsOnPage = this.currentPageAudits.map((audit) => audit.id);

    return selectableIdsOnPage.every((id) =>
      this.selectedAuditIds.includes(id),
    );
  }

  get isNew() {
    return this.criteriaIndex !== undefined ? false : true;
  }

  public mounted() {
    this.dirtyCriterions = _cloneDeep(this.reOrderedCriteriaArray);
  }

  public getFormattedText(value: string) {
    const lowerCaseValue = value.toLowerCase();
    return lowerCaseValue.charAt(0).toUpperCase() + lowerCaseValue.slice(1);
  }

  /**
   * Toggles the selection of all audits on the current page.
   * If the user checks the box, all current page audits are added to the selected list.
   * If the user unchecks the box, all current page audits are removed from the selected list.
   */
  public toggleSelectAllAudits(toggle: boolean): void {
    const currentAuditIds: number[] = this.currentPageAudits.map(
      (cpa) => cpa.id,
    );

    if (toggle) {
      this.selectedAuditIds.push(...currentAuditIds);
      this.selectedAuditIds = [...new Set(this.selectedAuditIds)];
      return;
    }

    this.selectedAuditIds = this.selectedAuditIds.filter(
      (id) => !currentAuditIds.includes(id),
    );
  }

  get showSourceLinkInput(): boolean {
    return !(
      this.currentCriteria &&
      this.currentCriteria.document &&
      this.currentCriteria.document.id !== ''
    );
  }

  get sourceLink() {
    const pattern = /^((http|https|ftp):\/\/)/;
    if (
      this.dirtyCriterion.content.sourceLink &&
      !pattern.test(this.dirtyCriterion.content.sourceLink)
    ) {
      return 'http://' + this.dirtyCriterion.content.sourceLink;
    } else {
      return this.dirtyCriterion.content.sourceLink;
    }
  }

  /**
   * Handler methods for click events
   * IMPROVEMENTS:
   * 1. Extracted inline template code to dedicated methods
   * 2. Improves readability of the template
   * 3. Enables better testing and reuse
   * 4. Following the principle of separation of concerns
   */
  public handleCreateClick(validator: any): void {
    validator.validate().then((validated: boolean) => {
      if (validated === false) return;
      this.addCriterion();
    });
  }

  public handleEditClick(validator: any): void {
    validator.validate().then((validated: boolean) => {
      if (validated === false) return;
      this.editCriterion();
    });
  }

  get areAllAuditsSelected(): boolean {
    return (
      this.selectedAuditIds.length > 0 &&
      this.selectedAuditIds.length === this.currentPageAudits.length
    );
  }

  /**
   * Computed property to safely expose criterion title to InteractiveModal.
   * Prevents "Avoid mutating prop directly" Vue warnings by creating
   * an intermediary between component data and child prop.
   */
  get criterionTitle(): string {
    return this.dirtyCriterion.content.title;
  }

  get isFormDirty(): boolean {
    return (
      JSON.stringify(this.criterion) !== JSON.stringify(this.dirtyCriterion)
    );
  }
}
</script>

<style lang="scss" module>
.sourceLink {
  color: #21b5ea;
  font-size: 14px;
  font-weight: bold;
}

.sourceLinkInputContainer {
  margin-bottom: 7px !important;
}

.addCriteria {
  width: 246px !important;
}

.modalNotification {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.modalNotification i {
  color: #f4c975;
}

.applyChangesContainer {
  padding-top: 1.5rem;
}

:global(.b-checkbox.checkbox .control-label) {
  font-weight: 400;
}

.applyChangesContainer {
  padding-top: 1.5rem;
}

.eligibleAuditsContainer {
  border: 1px solid #dfe2e5;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
}

.selectAllCheckboxContainer {
  border-radius: 8px 8px 0px 0px;
  padding: 1rem;
}

.eligibleAudits {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.noAvailableAuditsContainer {
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $font-grey;
  min-height: 2.5rem;
  padding: 1.5rem;
}

.missingAuditSelectionNotification {
  background-color: #e6e9ef;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border-radius: 10px;
}

:global(.b-checkbox.checkbox .control-label) {
  font-weight: 400;
}

.customPaginationOverride nav li {
  padding-right: 0 !important;
}
</style>
